import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { 
  MapPin, Phone, Clock, Shield, Star, CheckCircle, 
  Wrench, ThermometerSun, Award, Users, ArrowRight,
  Building, Zap, Calendar, MessageCircle, ChevronDown
} from 'lucide-react';
import { Button } from '../components/ui/button';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { arizaKodlari, getSikSorulanSorular, kombiMarkalari } from '../data/kombiData';

// İstanbul ilçeleri
const istanbulIlceler = [
  { slug: 'kadikoy', name: 'Kadıköy', population: '480.000+' },
  { slug: 'uskudar', name: 'Üsküdar', population: '530.000+' },
  { slug: 'besiktas', name: 'Beşiktaş', population: '180.000+' },
  { slug: 'sisli', name: 'Şişli', population: '270.000+' },
  { slug: 'beyoglu', name: 'Beyoğlu', population: '230.000+' },
  { slug: 'fatih', name: 'Fatih', population: '440.000+' },
  { slug: 'bakirkoy', name: 'Bakırköy', population: '220.000+' },
  { slug: 'atasehir', name: 'Ataşehir', population: '420.000+' },
  { slug: 'maltepe', name: 'Maltepe', population: '510.000+' },
  { slug: 'kartal', name: 'Kartal', population: '470.000+' },
  { slug: 'pendik', name: 'Pendik', population: '700.000+' },
  { slug: 'umraniye', name: 'Ümraniye', population: '700.000+' },
  { slug: 'sancaktepe', name: 'Sancaktepe', population: '450.000+' },
  { slug: 'sultanbeyli', name: 'Sultanbeyli', population: '330.000+' },
  { slug: 'tuzla', name: 'Tuzla', population: '270.000+' },
  { slug: 'cekmekoy', name: 'Çekmeköy', population: '250.000+' },
  { slug: 'beykoz', name: 'Beykoz', population: '250.000+' },
  { slug: 'sile', name: 'Şile', population: '40.000+' },
  { slug: 'avcilar', name: 'Avcılar', population: '430.000+' },
  { slug: 'kucukcekmece', name: 'Küçükçekmece', population: '790.000+' },
  { slug: 'bahcelievler', name: 'Bahçelievler', population: '600.000+' },
  { slug: 'bagcilar', name: 'Bağcılar', population: '750.000+' },
  { slug: 'gungoren', name: 'Güngören', population: '290.000+' },
  { slug: 'esenler', name: 'Esenler', population: '450.000+' },
  { slug: 'bayrampasa', name: 'Bayrampaşa', population: '270.000+' },
  { slug: 'eyupsultan', name: 'Eyüpsultan', population: '400.000+' },
  { slug: 'gaziosmanpasa', name: 'Gaziosmanpaşa', population: '490.000+' },
  { slug: 'sultangazi', name: 'Sultangazi', population: '530.000+' },
  { slug: 'kagithane', name: 'Kağıthane', population: '450.000+' },
  { slug: 'sariyer', name: 'Sarıyer', population: '350.000+' },
  { slug: 'arnavutkoy', name: 'Arnavutköy', population: '290.000+' },
  { slug: 'basaksehir', name: 'Başakşehir', population: '470.000+' },
  { slug: 'esenyurt', name: 'Esenyurt', population: '950.000+' },
  { slug: 'beylikduzu', name: 'Beylikdüzü', population: '350.000+' },
  { slug: 'buyukcekmece', name: 'Büyükçekmece', population: '250.000+' },
  { slug: 'catalca', name: 'Çatalca', population: '75.000+' },
  { slug: 'silivri', name: 'Silivri', population: '200.000+' },
  { slug: 'zeytinburnu', name: 'Zeytinburnu', population: '290.000+' },
  { slug: 'adalar', name: 'Adalar', population: '16.000+' },
];

// Hizmetler
const hizmetler = [
  { icon: Wrench, title: 'Kombi Arıza', desc: 'Tüm marka ve model kombilerde arıza tespiti ve onarım', price: '₺800\'den başlayan' },
  { icon: ThermometerSun, title: 'Kombi Bakım', desc: 'Yıllık periyodik bakım ve performans kontrolü', price: '₺1.200\'den başlayan' },
  { icon: Zap, title: 'Kombi Montaj', desc: 'Profesyonel kombi montaj ve devreye alma', price: '₺2.500\'den başlayan' },
  { icon: Building, title: 'Petek Temizliği', desc: 'Kalorifer petek temizliği ve bakımı', price: '₺400/petek' },
];

// Popüler aramalar
const populerAramalar = [
  'istanbul kombi servisi', 'kombi arıza', 'kombi bakımı', 'kombi tamiri',
  'kombi basıncı düşüyor', 'kombi su basmıyor', 'kombi yanmıyor', 'kombi F28 arızası',
  'acil kombi servisi', '7/24 kombi tamircisi', 'en yakın kombi ustası',
];

const IstanbulKombiServisi = () => {
  const navigate = useNavigate();
  const [selectedIlce, setSelectedIlce] = useState(null);
  const [selectedMarka, setSelectedMarka] = useState('vaillant');
  const [openFaq, setOpenFaq] = useState(null);
  
  const sikSorulanSorular = getSikSorulanSorular('İstanbul');

  useEffect(() => {
    const path = window.location.pathname;
    const match = path.match(/\/istanbul-(\w+)-kombi-servisi/);
    if (match && match[1] !== 'kombi') {
      const ilceSlug = match[1];
      const found = istanbulIlceler.find(i => i.slug === ilceSlug);
      setSelectedIlce(found || null);
    } else {
      setSelectedIlce(null);
    }
  }, []);

  // SEO için H1, Title ve Schema birebir uyumlu
  const cityName = selectedIlce ? `İstanbul ${selectedIlce.name}` : 'İstanbul';
  const primaryKeyword = `${cityName} Kombi Servisi`;
  
  const pageTitle = `${primaryKeyword} | 7/24 Aynı Gün Garanti – HizmetGeliyo`;

  const pageDescription = selectedIlce
    ? `${selectedIlce.name} kombi servisi ✓ 7/24 acil servis ✓ Aynı gün hizmet garantisi ✓ Vaillant, Bosch, Baymak, Demirdöküm tüm markalarda arıza, bakım ve montaj. ☎ 0850 242 78 08`
    : `İstanbul kombi servisi ✓ 500+ onaylı usta ✓ 39 ilçede aynı gün servis garantisi ✓ Tüm markalarda profesyonel arıza, bakım ve montaj hizmeti. ☎ 0850 242 78 08`;

  const canonicalUrl = selectedIlce 
    ? `https://hizmetgeliyo.com/istanbul-${selectedIlce.slug}-kombi-servisi`
    : 'https://hizmetgeliyo.com/istanbul-kombi-servisi';

  // Schema.org JSON-LD - Title ve H1 ile birebir uyumlu
  const schemaData = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": canonicalUrl,
    "name": primaryKeyword,
    "image": "https://hizmetgeliyo.com/logo.webp",
    "description": pageDescription,
    "url": canonicalUrl,
    "telephone": "+908502427808",
    "priceRange": "₺₺",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": selectedIlce ? selectedIlce.name : "İstanbul",
      "addressRegion": "İstanbul",
      "addressCountry": "TR"
    },
    "areaServed": {
      "@type": "City",
      "name": "İstanbul"
    },
    "openingHoursSpecification": {
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
      "opens": "00:00",
      "closes": "23:59"
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.9",
      "reviewCount": "5847"
    }
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": `${selectedIlce ? selectedIlce.name + "'de" : "İstanbul'da"} kombi servisi ne kadar sürede gelir?`,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Acil durumlarda 1-2 saat içinde, randevulu işlemlerde aynı gün içinde ustamız adresinize gelir."
        }
      },
      {
        "@type": "Question",
        "name": "Kombi bakım ücreti ne kadar?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Kombi bakım ücreti marka ve modele göre 800₺'den başlamaktadır. Kesin fiyat için ücretsiz keşif hizmeti sunuyoruz."
        }
      },
      {
        "@type": "Question",
        "name": "Hangi marka kombilere servis veriyorsunuz?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Vaillant, Bosch, Baymak, Demirdöküm, Ariston, Buderus, Viessmann, Ferroli, Alarko, ECA ve tüm markalara servis veriyoruz."
        }
      }
    ]
  };

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta name="keywords" content={`istanbul kombi servisi, ${selectedIlce ? selectedIlce.name.toLowerCase() + ' kombi servisi, ' : ''}kombi ariza, kombi bakim, kombi tamiri, vaillant servisi, bosch servisi`} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="tr_TR" />
        <script type="application/ld+json">
          {JSON.stringify(schemaData)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      </Helmet>

      <Navbar />
      
      <main className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-[#0A1628] via-[#1a2d4a] to-[#0A1628] text-white overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)', backgroundSize: '40px 40px' }} />
          </div>
          
          <div className="relative max-w-7xl mx-auto px-4 py-16 lg:py-24">
            <div className="max-w-3xl mx-auto text-center">
              <nav className="flex items-center justify-center gap-2 text-sm text-white/60 mb-6">
                <Link to="/" className="hover:text-white transition">Ana Sayfa</Link>
                <span>/</span>
                <Link to="/istanbul-kombi-servisi" className="hover:text-white transition">İstanbul Kombi Servisi</Link>
                {selectedIlce && (<><span>/</span><span className="text-orange-400">{selectedIlce.name}</span></>)}
              </nav>

              <div className="inline-flex items-center gap-2 bg-orange-500/20 border border-orange-500/30 rounded-full px-4 py-2 mb-6">
                <Clock className="w-4 h-4 text-orange-400" />
                <span className="text-orange-300 text-sm font-medium">7/24 Acil Servis Hizmeti</span>
              </div>

              <h1 className="text-4xl lg:text-5xl xl:text-6xl font-black mb-6 leading-tight">
                {selectedIlce ? (
                  <><span className="text-orange-500">{cityName}</span><br />Kombi Servisi</>
                ) : (
                  <><span className="text-orange-500">İstanbul</span> Kombi Servisi</>
                )}
              </h1>
              
              <p className="text-2xl lg:text-3xl text-white/90 mb-6 font-semibold">
                7/24 Aynı Gün Servis Garantisi
              </p>

              <p className="text-lg lg:text-xl text-white/80 mb-8 max-w-xl mx-auto">
                {selectedIlce 
                  ? `${selectedIlce.name} ve çevresinde ${selectedIlce.population} kişiye hizmet veriyoruz.`
                  : 'İstanbul\'un 39 ilçesinde profesyonel kombi servisi. En yakın ustayı anında yönlendiriyoruz.'}
              </p>

              <div className="flex flex-wrap justify-center gap-4 mb-8">
                <div className="flex items-center gap-2 bg-white/10 backdrop-blur rounded-lg px-4 py-2">
                  <Shield className="w-5 h-5 text-green-400" /><span className="text-sm">1 Yıl Garanti</span>
                </div>
                <div className="flex items-center gap-2 bg-white/10 backdrop-blur rounded-lg px-4 py-2">
                  <Star className="w-5 h-5 text-yellow-400" /><span className="text-sm">4.9 Puan</span>
                </div>
                <div className="flex items-center gap-2 bg-white/10 backdrop-blur rounded-lg px-4 py-2">
                  <Users className="w-5 h-5 text-blue-400" /><span className="text-sm">500+ Onaylı Usta</span>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row justify-center gap-4">
                <Button onClick={() => navigate('/servis-talep/kombi')} className="bg-orange-500 hover:bg-orange-600 text-white font-bold text-lg px-8 py-4 rounded-xl shadow-lg shadow-orange-500/30 h-14">
                  <Calendar className="w-5 h-5 mr-2" />Hemen Randevu Al
                </Button>
                <a href="https://wa.me/908502427808?text=Merhaba,%20İstanbul%20kombi%20servisi%20almak%20istiyorum." target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 bg-green-500 hover:bg-green-600 text-white font-bold text-lg px-8 py-4 rounded-xl shadow-lg shadow-green-500/30 transition h-14">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                  WhatsApp
                </a>
                <a href="tel:+908502427808" className="flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 backdrop-blur border border-white/20 text-white font-bold text-lg px-8 py-4 rounded-xl transition h-14">
                  <Phone className="w-5 h-5" />0850 242 78 08
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* İlçe Seçimi */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl lg:text-4xl font-bold text-slate-800 mb-4">İlçeni Seç, <span className="text-orange-500">Hemen Talep Oluştur</span></h2>
              <p className="text-slate-600 text-lg max-w-2xl mx-auto">İstanbul'un 39 ilçesinde profesyonel kombi servisi sunuyoruz.</p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-3">
              {istanbulIlceler.slice(0, 28).map((i) => (
                <button key={i.slug} onClick={() => navigate(`/servis-talep/kombi?il=İstanbul&ilce=${encodeURIComponent(i.name)}`)}
                  className={`group relative p-4 rounded-xl border-2 transition-all hover:shadow-lg hover:-translate-y-1 ${selectedIlce?.slug === i.slug ? 'border-orange-500 bg-orange-50 shadow-lg' : 'border-slate-200 hover:border-orange-300 bg-white'}`}>
                  <div className="text-center">
                    <MapPin className={`w-6 h-6 mx-auto mb-2 ${selectedIlce?.slug === i.slug ? 'text-orange-500' : 'text-slate-400 group-hover:text-orange-500'}`} />
                    <h3 className="font-bold text-slate-800 text-sm">{i.name}</h3>
                    <p className="text-xs text-slate-500 mt-1">{i.population}</p>
                  </div>
                  {selectedIlce?.slug === i.slug && (<div className="absolute -top-2 -right-2 w-6 h-6 bg-orange-500 rounded-full flex items-center justify-center"><CheckCircle className="w-4 h-4 text-white" /></div>)}
                </button>
              ))}
            </div>
            <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-3">
              {istanbulIlceler.slice(28).map((i) => (
                <button key={i.slug} onClick={() => navigate(`/servis-talep/kombi?il=İstanbul&ilce=${encodeURIComponent(i.name)}`)}
                  className="group p-4 rounded-xl border-2 border-slate-200 hover:border-orange-300 bg-white transition-all hover:shadow-lg hover:-translate-y-1">
                  <div className="text-center">
                    <MapPin className="w-6 h-6 mx-auto mb-2 text-slate-400 group-hover:text-orange-500" />
                    <h3 className="font-bold text-slate-800 text-sm">{i.name}</h3>
                    <p className="text-xs text-slate-500 mt-1">{i.population}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Hizmetler */}
        <section className="py-16 bg-slate-50">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl lg:text-4xl font-bold text-slate-800 mb-4">Kombi <span className="text-orange-500">Hizmetlerimiz</span></h2>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {hizmetler.map((hizmet, idx) => (
                <div key={idx} className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all hover:-translate-y-1 border border-slate-100">
                  <div className="w-14 h-14 bg-orange-100 rounded-xl flex items-center justify-center mb-4">
                    <hizmet.icon className="w-7 h-7 text-orange-500" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-800 mb-2">{hizmet.title}</h3>
                  <p className="text-slate-600 text-sm mb-4">{hizmet.desc}</p>
                  <div className="text-orange-500 font-bold">{hizmet.price}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Markalar */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl lg:text-4xl font-bold text-slate-800 mb-4">Tüm <span className="text-orange-500">Markalarda</span> Uzmanız</h2>
            </div>
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-4">
              {kombiMarkalari.map((marka, idx) => (
                <Link key={idx} to={`/servis-talep/kombi?marka=${marka.name.toLowerCase()}`} className="group bg-white hover:bg-slate-50 rounded-xl p-4 text-center transition-all hover:shadow-lg border border-slate-200 hover:border-orange-300">
                  <div className="w-full h-14 mx-auto mb-2 flex items-center justify-center">
                    {marka.logo ? (
                      <img src={marka.logo} alt={`${marka.name} Logo`} className="max-h-12 max-w-full object-contain group-hover:scale-110 transition-transform" onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }} />
                    ) : null}
                    <div className={`w-full h-12 rounded-lg items-center justify-center ${marka.logo ? 'hidden' : 'flex'}`} style={{ backgroundColor: marka.color + '15' }}>
                      <span className="text-base font-bold" style={{ color: marka.color }}>{marka.name}</span>
                    </div>
                  </div>
                  <h3 className="font-semibold text-slate-700 group-hover:text-orange-500 transition-colors text-sm">{marka.name}</h3>
                  <p className="text-xs text-slate-400 mt-0.5">Servisi</p>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Arıza Kodları - Modern SEO */}
        <section className="py-16 bg-gradient-to-br from-slate-900 to-slate-800" id="ariza-kodlari">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-12">
              <span className="inline-block bg-red-500/20 text-red-400 text-sm font-semibold px-4 py-2 rounded-full mb-4">
                🔧 En Kapsamlı Arıza Rehberi
              </span>
              <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
                Kombi <span className="text-orange-500">Arıza Kodları</span> ve Çözümleri
              </h2>
              <p className="text-slate-400 text-lg max-w-2xl mx-auto">
                Kombinizde hata kodu mu görüyorsunuz? Markanızı seçin ve çözümünü öğrenin. 8 marka, 50+ arıza kodu!
              </p>
            </div>

            {/* Marka Tab'ları */}
            <div className="flex flex-wrap justify-center gap-2 mb-8">
              {Object.keys(arizaKodlari).map((marka) => (
                <button
                  key={marka}
                  onClick={() => setSelectedMarka(marka)}
                  className={`px-4 py-2 rounded-full font-medium transition-all ${
                    selectedMarka === marka 
                      ? 'bg-orange-500 text-white shadow-lg shadow-orange-500/30' 
                      : 'bg-white/10 text-white/70 hover:bg-white/20'
                  }`}
                >
                  {marka.charAt(0).toUpperCase() + marka.slice(1)}
                </button>
              ))}
            </div>

            {/* Seçili Marka Arıza Kodları */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {arizaKodlari[selectedMarka]?.map((ariza, idx) => (
                <div 
                  key={idx}
                  className={`bg-white/5 backdrop-blur rounded-xl p-5 border transition-all hover:scale-[1.02] ${
                    ariza.oncelik === 'high' ? 'border-red-500/50 hover:border-red-500' :
                    ariza.oncelik === 'medium' ? 'border-yellow-500/50 hover:border-yellow-500' :
                    'border-slate-600/50 hover:border-slate-500'
                  }`}
                >
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0">
                      <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${
                        ariza.oncelik === 'high' ? 'bg-red-500/20' :
                        ariza.oncelik === 'medium' ? 'bg-yellow-500/20' :
                        'bg-slate-500/20'
                      }`}>
                        <span className={`font-bold text-lg ${
                          ariza.oncelik === 'high' ? 'text-red-400' :
                          ariza.oncelik === 'medium' ? 'text-yellow-400' :
                          'text-slate-400'
                        }`}>{ariza.kod}</span>
                      </div>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-bold text-white">{ariza.anlam}</h3>
                        {ariza.oncelik === 'high' && (
                          <span className="text-xs bg-red-500/30 text-red-300 px-2 py-0.5 rounded">Acil</span>
                        )}
                      </div>
                      <p className="text-sm text-slate-400">{ariza.cozum}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* CTA */}
            <div className="mt-12 text-center">
              <div className="bg-gradient-to-r from-orange-500/20 to-red-500/20 backdrop-blur rounded-2xl p-8 max-w-2xl mx-auto">
                <h3 className="text-2xl font-bold text-white mb-2">Arıza Kodunuz Çözülmedi mi?</h3>
                <p className="text-slate-400 mb-6">Uzman ekibimiz 7/24 yanınızda. Hemen arayın, aynı gün çözüm!</p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <a href="tel:08502427808" className="inline-flex items-center justify-center gap-2 bg-white text-slate-900 font-bold px-6 py-3 rounded-xl hover:bg-slate-100 transition">
                    <Phone className="w-5 h-5" /> 0850 242 78 08
                  </a>
                  <Button
                    onClick={() => navigate('/servis-talep/kombi')}
                    className="bg-orange-500 hover:bg-orange-600 text-white font-bold px-6 py-3 rounded-xl"
                  >
                    Online Servis Talebi
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Neden Biz */}
        <section className="py-16 bg-gradient-to-br from-[#0A1628] to-[#1a2d4a] text-white">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl lg:text-4xl font-bold mb-4">Neden <span className="text-orange-500">HizmetGeliyo</span>?</h2>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { icon: Clock, title: 'Aynı Gün Servis', desc: 'Sabah arayın, akşama ustanız kapınızda' },
                { icon: Shield, title: '1 Yıl Garanti', desc: 'Tüm işlemlerde 1 yıl parça ve işçilik garantisi' },
                { icon: Award, title: 'Uzman Ustalar', desc: '500+ onaylı, sertifikalı kombi ustası' },
                { icon: Star, title: '4.9 Müşteri Puanı', desc: '10.000+ mutlu müşteri' },
              ].map((item, idx) => (
                <div key={idx} className="bg-white/10 backdrop-blur rounded-2xl p-6 text-center hover:bg-white/15 transition">
                  <div className="w-16 h-16 bg-orange-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <item.icon className="w-8 h-8 text-orange-400" />
                  </div>
                  <h3 className="text-xl font-bold mb-2">{item.title}</h3>
                  <p className="text-white/70">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* SSS - SEO için detaylı */}
        <section className="py-16 bg-slate-50" id="sss">
          <div className="max-w-4xl mx-auto px-4">
            <div className="text-center mb-12">
              <span className="inline-block bg-orange-100 text-orange-600 text-sm font-semibold px-4 py-2 rounded-full mb-4">
                ❓ Merak Edilenler
              </span>
              <h2 className="text-3xl lg:text-4xl font-bold text-slate-800 mb-4">
                Sık Sorulan <span className="text-orange-500">Sorular</span>
              </h2>
              <p className="text-slate-600">
                Kombi servisine dair en çok sorulan sorular ve uzman cevapları
              </p>
            </div>

            <div className="space-y-3">
              {sikSorulanSorular.map((faq, idx) => (
                <div 
                  key={idx} 
                  className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden"
                >
                  <button
                    onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                    className="w-full flex items-center justify-between p-5 text-left hover:bg-slate-50 transition"
                  >
                    <h3 className="font-bold text-slate-800 text-lg pr-4">{faq.soru}</h3>
                    <ChevronDown className={`w-5 h-5 text-slate-400 flex-shrink-0 transition-transform ${openFaq === idx ? 'rotate-180' : ''}`} />
                  </button>
                  {openFaq === idx && (
                    <div className="px-5 pb-5 border-t border-slate-100">
                      <p className="text-slate-600 pt-4 leading-relaxed">{faq.cevap}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-orange-500">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">{selectedIlce ? `${selectedIlce.name}'de Kombi Sorununuz mu Var?` : 'Kombi Sorununuz mu Var?'}</h2>
            <p className="text-white/90 text-lg mb-8">Hemen bizi arayın, aynı gün çözüm sunalım.</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button onClick={() => navigate('/servis-talep/kombi')} className="bg-white text-orange-500 hover:bg-slate-100 font-bold text-lg px-8 py-4 rounded-xl h-14">
                Online Randevu Al<ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <a href="https://wa.me/908502427808" target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 bg-green-500 hover:bg-green-600 text-white font-bold text-lg px-8 py-4 rounded-xl transition h-14">
                <MessageCircle className="w-5 h-5" />WhatsApp
              </a>
              <a href="tel:+908502427808" className="flex items-center justify-center gap-2 bg-white/20 hover:bg-white/30 text-white font-bold text-lg px-8 py-4 rounded-xl transition h-14">
                <Phone className="w-5 h-5" />0850 242 78 08
              </a>
            </div>
          </div>
        </section>

        {/* Popüler Aramalar */}
        <section className="py-12 bg-slate-50">
          <div className="max-w-7xl mx-auto px-4">
            <h3 className="text-xl font-bold text-slate-800 mb-6 text-center">İlgili Aramalar</h3>
            <div className="flex flex-wrap justify-center gap-2">
              {populerAramalar.map((arama, idx) => (
                <Link key={idx} to={`/servis-talep/kombi?q=${encodeURIComponent(arama)}`} className="text-sm bg-white text-slate-600 hover:text-orange-500 hover:border-orange-300 px-4 py-2 rounded-full border border-slate-200 transition-all">{arama}</Link>
              ))}
            </div>
          </div>
        </section>

        {/* Diğer İlçeler */}
        <section className="py-12 bg-white border-t">
          <div className="max-w-7xl mx-auto px-4">
            <h3 className="text-xl font-bold text-slate-800 mb-6 text-center">İstanbul'un Tüm İlçelerinde Hizmet Veriyoruz</h3>
            <div className="flex flex-wrap justify-center gap-3">
              {istanbulIlceler.map((i) => (
                <Link key={i.slug} to={`/istanbul-${i.slug}-kombi-servisi`} className="text-sm text-slate-600 hover:text-orange-500 hover:underline">{i.name} Kombi Servisi</Link>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
};

export default IstanbulKombiServisi;
